import { useEffect, useMemo, useState } from "react";
import ExternalCodesField from "../ManageImmunization/common/ExternalCodesField";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import api from "api/axios";

const ALL_SPECIALITIES_LABEL = "All Specialities";

const NAME_PATTERN = /^[a-zA-Z0-9\s/\\&\-(),.']+$/;

const toStringList = (subComplaints) =>
  (Array.isArray(subComplaints) ? subComplaints : [])
    .map((item) => (typeof item === "string" ? item : item?.name))
    .filter(Boolean);

const dedupe = (values) => {
  const seen = new Set();
  const result = [];

  values.forEach((value) => {
    const clean = String(value || "").trim();
    const key = clean.toLowerCase();
    if (clean && !seen.has(key)) {
      seen.add(key);
      result.push(clean);
    }
  });

  return result;
};

const ComplaintModal = ({ open, onClose, data }) => {
  const complaintId = data?._id;
  const isUpdate = Boolean(complaintId);

  const [name, setName] = useState("");
  const [subComplaints, setSubComplaints] = useState([]);
  // SNOMED / ICD-10 codes for the global complaint.
  const [externalCodes, setExternalCodes] = useState([]);
  const [speciality, setSpeciality] = useState([]);
  const [allSpecialities, setAllSpecialities] = useState(false);
  const [specialityOptions, setSpecialityOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;

    setName(data?.name || "");
    setSubComplaints(toStringList(data?.subComplaints));
    setExternalCodes(data?.externalCodes || []);

    const mapped = Array.isArray(data?.speciality)
      ? data.speciality.filter((item) => item && typeof item === "object")
      : [];

    setSpeciality(mapped);
    // An existing complaint with no specialities is an "All Specialities" one.
    setAllSpecialities(isUpdate ? mapped.length === 0 : false);
    setErrors({});
  }, [open, data, isUpdate]);

  useEffect(() => {
    if (!open) return;

    api
      .get("/specialization")
      .then((res) => {
        const list = res?.data?.data?.Specialization;
        setSpecialityOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => toast.error("Could not load specialities"));
  }, [open]);

  const trimmedName = name.trim();

  const validationError = useMemo(() => {
    if (!trimmedName) return "Complaint category is required";
    if (trimmedName.length < 2) return "Use at least 2 characters";
    if (trimmedName.length > 200) return "Use at most 200 characters";
    if (!NAME_PATTERN.test(trimmedName)) {
      return "Only letters, numbers and - / & ( ) , . ' are allowed";
    }
    if (allSpecialities && speciality.length > 0) {
      return `"${ALL_SPECIALITIES_LABEL}" cannot be combined with specific specialities`;
    }
    if (!allSpecialities && speciality.length === 0) {
      return `Select at least one speciality, or choose "${ALL_SPECIALITIES_LABEL}"`;
    }
    return "";
  }, [trimmedName, allSpecialities, speciality]);

  const handleSubmit = async () => {
    if (validationError) {
      setErrors({ form: validationError });
      return;
    }

    setSaving(true);

    const payload = {
      name: trimmedName,
      externalCodes,
      subComplaints: dedupe(subComplaints).map((value) => ({ name: value })),
      speciality: allSpecialities ? [] : speciality.map((item) => item._id),
      allSpecialities,
    };

    try {
      if (isUpdate) {
        await api.put(`/admin/complaints/${complaintId}`, payload);
        toast.success("Complaint updated successfully");
      } else {
        await api.post("/admin/complaints", payload);
        toast.success("Complaint created successfully");
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} complaint`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose("close")}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isUpdate ? "Edit Complaint" : "Add Complaint"}
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          <TextField
            label="Complaint Category"
            required
            fullWidth
            size="small"
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={Boolean(errors.form) && !trimmedName}
          />

          <Autocomplete
            multiple
            freeSolo
            options={[]}
            size="small"
            value={subComplaints}
            onChange={(event, next) => setSubComplaints(dedupe(next))}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  size="small"
                  label={option}
                  {...getTagProps({ index })}
                  key={option}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sub Complaints"
                placeholder="Type and press Enter"
                helperText="Optional. Press Enter after each sub-complaint."
              />
            )}
          />

          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            options={specialityOptions}
            value={speciality}
            disabled={allSpecialities}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            onChange={(event, next) => setSpeciality(next)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Specialities"
                placeholder={
                  allSpecialities ? ALL_SPECIALITIES_LABEL : "Select specialities"
                }
              />
            )}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={allSpecialities}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setAllSpecialities(checked);
                  if (checked) setSpeciality([]);
                }}
              />
            }
            label={`${ALL_SPECIALITIES_LABEL} (visible to every doctor)`}
          />

          {validationError && (
            <Box sx={{ color: "#d32f2f", fontSize: "0.8rem" }}>
              {validationError}
            </Box>
          )}
        </Box>
      </DialogContent>

      <Box sx={{ px: 1, pb: 1 }}>
        <ExternalCodesField
          value={externalCodes}
          onChange={setExternalCodes}
        />
      </Box>
      <DialogActions sx={{ padding: "1rem 1.5rem" }}>
        <Button onClick={() => onClose("close")} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || Boolean(validationError)}
          sx={{ bgcolor: "#194AF5", ":hover": { bgcolor: "#1540d6" } }}
        >
          {saving ? <CircularProgress size="1.2rem" sx={{ color: "#fff" }} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComplaintModal;
